import { useEffect, useState } from 'react';
import alsaariLogo from '@/assets/carriers/alsaari.svg';
import hiexpressLogo from '@/assets/carriers/hiexpress.svg';
import sandoogLogo from '@/assets/carriers/sandoog.svg';
import waseetLogo from '@/assets/carriers/waseet.svg';
import zaeemLogo from '@/assets/carriers/zaeem.svg';

/**
 * Carrier logo with initials fallback. Stays consistent across the
 * carriers tab and the rule-builder modal so a missing or broken logo
 * always degrades to the same colored badge instead of an empty box
 * or a broken-image icon.
 *
 * SR-FB3 (May 2026 feedback) — merchants asked to see logos on every
 * carrier chip, but the backend's `avatar.abs_url` field is only set
 * for some carriers. We keep the backend logo as the primary source
 * and add a slug-keyed local-SVG fallback for the carriers we already
 * ship icons for. Carriers with neither still degrade to the existing
 * colored-initials badge.
 */
interface CarrierAvatarProps {
  src?: string | null;
  name?: string | null;
  slug?: string | null;
  size?: number;
  className?: string;
}

const LOCAL_LOGOS: Record<string, string> = {
  alsaari: alsaariLogo,
  sari: alsaariLogo, // backend slug for Al-Saari
  'hi-express': hiexpressLogo,
  hiexpress: hiexpressLogo,
  sandoog: sandoogLogo,
  waseet: waseetLogo,
  wasset: waseetLogo, // backend uses "wasset"
  zaeem: zaeemLogo,
};

const getInitials = (raw: string): string => {
  const cleaned = raw
    .replace(/[-_]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (cleaned.length === 0) return '??';
  if (cleaned.length >= 2) return (cleaned[0][0] + cleaned[1][0]).toUpperCase();
  return cleaned[0].slice(0, 2).toUpperCase();
};

// Stable color from a string so the same carrier always gets the same
// background color even when the logo fails to load.
const slugToColor = (key: string): string => {
  const palette = [
    'bg-blue-100 text-blue-700',
    'bg-emerald-100 text-emerald-700',
    'bg-amber-100 text-amber-700',
    'bg-rose-100 text-rose-700',
    'bg-violet-100 text-violet-700',
    'bg-cyan-100 text-cyan-700',
    'bg-pink-100 text-pink-700',
    'bg-orange-100 text-orange-700',
    'bg-teal-100 text-teal-700',
    'bg-indigo-100 text-indigo-700',
  ];
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) & 0xffff;
  return palette[h % palette.length];
};

export function CarrierAvatar({
  src,
  name,
  slug,
  size = 40,
  className = '',
}: CarrierAvatarProps) {
  const [errored, setErrored] = useState(false);
  // Reset error state when src changes (avatar swap mid-session).
  useEffect(() => {
    setErrored(false);
  }, [src]);

  const display = (name && name.trim()) || (slug && slug.trim()) || '';
  const initials = getInitials(display);
  const colorClasses = slugToColor((slug ?? name ?? 'x').toLowerCase());
  const localLogo = slug ? LOCAL_LOGOS[slug.toLowerCase()] : undefined;
  const effectiveSrc = (src && !errored) ? src : localLogo;

  if (effectiveSrc) {
    return (
      <img
        src={effectiveSrc}
        alt={display}
        loading='lazy'
        onError={() => setErrored(true)}
        className={`object-contain bg-sd-muted/50 rounded-lg ${className}`}
        style={{ width: size, height: size }}
      />
    );
  }

  return (
    <div
      role='img'
      aria-label={display}
      className={`inline-flex items-center justify-center rounded-lg font-bold ${colorClasses} ${className}`}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.4) }}
    >
      {initials}
    </div>
  );
}
